<template>
  <modal
    name="addParticipantsModal"
    class="add-participants-modal"
    :width="600"
    :focus-trap="true"
    :height="630"
    @opened="loadEligibleParticipants"
    v-model="show"
  >
    <div class="position-relative modal-outer-container">
      <div class="modal-header border-bottom">
        <div>Add Participants</div>
        <button type="button" class="btn border-0" @click="closeModal">
          <i class="fas fa-times fa-lg"></i>
        </button>
      </div>
      <div class="body-inner-container">
        <div class="card-body">
          <div class="form-group">
            <p class="text-muted mb-2" v-if="session">
              Available slots:
              {{ session.capacity - currentParticipantsCount }}
            </p>
            <label>Select {{ customerLabel }}s</label>
            <div v-if="clientsLoading" class="text-center py-5">
              <i class="fas fa-spin fa-circle-notch fa-2x text-muted"></i>
            </div>
            <inline-multi-select
              v-else
              v-model="selectedClients"
              :options="dropdownClients"
              :placeholder="`Search by ${customerLabel.toLowerCase()} name`"
              :noResultMessage="`No ${customerLabel.toLowerCase()}s found`"
              value-key="value"
              label-key="label"
              :showSelectAll="false"
              height="275px"
            />
          </div>
          <div class="d-flex flex-md-row flex-column">
            <div class="form-group mr-md-4 mb-3">
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="feeRequiredNew"
                  v-model="newParticipantsFeeRequired"
                />
                <label class="custom-control-label" for="feeRequiredNew">
                  Fee Required
                </label>
              </div>
            </div>
            <div class="form-group mb-3" v-if="showSendInvitationsOption">
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="sendInvitationsNew"
                  v-model="sendInvitations"
                />
                <label class="custom-control-label" for="sendInvitationsNew">
                  Send Invitation Emails
                </label>
              </div>
            </div>
          </div>
          <div class="form-group px-2 mb-0">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="allowOverlapParticipants"
                v-model="allowOverlap"
              />
              <label
                class="custom-control-label text-dark-red"
                for="allowOverlapParticipants"
                >Allow Overlapping With Other Sessions</label
              >
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer border-top">
        <button type="button" class="btn btn-secondary" @click="closeModal">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="addParticipantsHandler"
          :disabled="addParticipantsLoading || selectedClients.length === 0"
        >
          <i
            class="fas fa-spin fa-circle-notch"
            v-if="addParticipantsLoading"
          ></i>
          Add Selected
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import InlineMultiSelect from "@/components/InlineMultiSelect.vue";
import * as dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { mapActions, mapState } from "vuex";
dayjs.extend(utc);
dayjs.extend(timezone);

export default {
  name: "AddParticipantsModal",
  components: {
    InlineMultiSelect,
  },
  props: {
    session: {
      type: Object,
      default: null,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "participants-added"],
  data() {
    return {
      selectedClients: [],
      dropdownClients: [],
      clientsLoading: false,
      newParticipantsFeeRequired: true,
      sendInvitations: false,
      allowOverlap: false,
    };
  },
  computed: {
    ...mapState({
      addParticipantsLoading: (state) =>
        state.groupSessions.addParticipantsLoading,
      currentParticipantsCount: (state) =>
        state.groupSessions.participants.data.length,
    }),
    customerLabel() {
      try {
        return (
          this.$customerLabel?.({ type: "singular", capitalize: true }) ||
          "Client"
        );
      } catch (e) {
        return "Client";
      }
    },
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    showSendInvitationsOption() {
      if (!this.session || !this.session.start_time) return false;
      const now = dayjs().tz("America/Toronto");
      const startTime = dayjs(this.session.start_time).tz("America/Toronto");
      return startTime.isAfter(now);
    },
  },
  methods: {
    ...mapActions({
      addParticipants: "groupSessions/addParticipants",
      getEligibleParticipants: "groupSessions/getEligibleParticipants",
    }),
    async loadEligibleParticipants() {
      if (!this.session) return;
      this.clientsLoading = true;
      const participants = await this.getEligibleParticipants({
        sessionId: this.session.id,
      });
      this.dropdownClients = (participants || []).map((p) => ({
        value: p.id,
        label: p.name,
      }));
      this.clientsLoading = false;
    },
    addParticipantsHandler() {
      if (!this.session || this.selectedClients.length === 0) {
        return;
      }

      this.addParticipants({
        sessionId: this.session.id,
        userIds: this.selectedClients,
        isFeeRequired: this.newParticipantsFeeRequired,
        sendNotification: this.sendInvitations,
        allowOverlap: this.allowOverlap,
      }).then((res) => {
        if (res) {
          this.closeModal();
          this.$emit("participants-added");
        }
      });
    },
    closeModal() {
      this.selectedClients = [];
      this.newParticipantsFeeRequired = true;
      this.sendInvitations = false;
      this.allowOverlap = false;
      this.dropdownClients = [];
      this.show = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-outer-container {
  display: flex;
  flex-direction: column;
  height: 100%;

  .body-inner-container {
    flex: 1;
    overflow-y: auto;

    .card-body {
      padding: 1rem;
    }
  }
}
</style>

<style lang="scss">
.add-participants-modal {
  z-index: 9999 !important;
  .v--modal {
    overflow: visible !important;
  }
  .ccc-modal-content {
    background-color: #ffffff;
  }
}
</style>
